import { Icon } from "@studio3t/generic-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TestSuiteCase } from "@/types/api";
import { generatedCasesFromJson } from "./aiHelperContext";
import {
  type InsertState,
  type InsertTarget,
  insertDestination,
  insertReplaces,
  insertTarget,
  pipelineLandingWait,
  withFreeTestNames,
} from "./aiInsertTarget";
import { testRowsFromSuiteCase, usePipelineStaging } from "./pipelineStaging";
import {
  type StageTabId,
  TRANSFORM_EDITOR_ID,
  tabDefaultRow,
} from "./stagingModel";
import { useEnsureStagedSuite, useSuiteCatalog } from "./useTestSuiteSync";

// The AI Helper's Insert (TTTL-217): aiInsertTarget decides where a fence
// lands, this hook lands it the way a user would — on the target's tab
// first, with the target row or slot selected so its editor is the one
// on screen, THEN the write, then the caret. Each step needs the render
// before it to have settled (a tab switch restores that tab's selection,
// a selection swaps the editor), so a landing is a small state machine
// driven by effects rather than one call. Unsaved work in the way is
// confirmed first (pending). Nothing is saved here — the draft still
// needs the user's Save.

/** An insert waiting on the user's Replace/Cancel. */
export interface PendingReplace {
  /** What the insert would overwrite ("your edited input document"). */
  replaces: string;
  /** Where it goes ("the Source input", "mask_pii.rhai"). */
  destination: string;
}

export interface InsertHost {
  apply: (
    code: string,
    lang: string,
    artifact: string | null,
    name: string | null,
  ) => void;
  pending: PendingReplace | null;
  confirmPending: () => void;
  cancelPending: () => void;
}

type Staging = ReturnType<typeof usePipelineStaging>;

// A landing in progress. `write` landings select a row on a tab and then
// hand the code to that editor's setter; `selected` marks the one
// selection attempt each landing gets.
type Landing =
  | {
      kind: "write";
      tab: StageTabId;
      row: string;
      editor: "script" | "schema";
      code: string;
      selected: boolean;
    }
  | { kind: "input"; code: string; selected: boolean }
  /** `started`: the catalog write is in flight — the landing holds until
   *  it settles so a second Insert can't run against the same pending
   *  suite. */
  | { kind: "tests"; cases: TestSuiteCase[]; started: boolean }
  | { kind: "pipeline" };

type LandingStep = {
  landing: Landing | null;
  settle: (next: Landing | null) => void;
};

function useInsertState(generatedCode: string): InsertState {
  const s = usePipelineStaging();
  return useMemo(
    () => ({
      stageTab: s.stageTab,
      editorKind: s.editorKind,
      effectiveSelectedId: s.effectiveSelectedId,
      transforms: s.transforms,
      schemas: s.schemas,
      scriptDrafts: s.scriptDrafts,
      schemaDrafts: s.schemaDrafts,
      schemaDocument: s.schemaDocument,
      schemaOrigin: s.schemaOrigin,
      inputDocument: s.inputDocument,
      inputOrigin: s.inputOrigin,
      savedInput: s.savedInput,
      pipelineCodeDraft: s.pipelineCodeDraft,
      generatedCode,
    }),
    [
      s.stageTab,
      s.editorKind,
      s.effectiveSelectedId,
      s.transforms,
      s.schemas,
      s.scriptDrafts,
      s.schemaDrafts,
      s.schemaDocument,
      s.schemaOrigin,
      s.inputDocument,
      s.inputOrigin,
      s.savedInput,
      s.pipelineCodeDraft,
      generatedCode,
    ],
  );
}

// One selection attempt per landing: the wanted row is selected on the
// tab, and the write goes ahead on the next render whether or not the
// selection took (a row that can't be selected must not stall the
// insert) — the document setters write to whatever is selected then.
function selectOnce(
  step: LandingStep,
  landing: Landing & { selected: boolean },
  wanted: string,
  staging: Staging,
): boolean {
  if (landing.selected || staging.effectiveSelectedId === wanted) return false;
  step.settle({ ...landing, selected: true } as Landing);
  staging.toggleSelected(wanted);
  return true;
}

// A script lands on its row (the TRANSFORM pseudo-row for a fresh untitled
// one — the setter mints the row from there), a schema in its slot, once
// the tab is up, the row is selected AND its stored content has arrived:
// selecting a clean staged row fetches its saved text, and a write made
// before that lands would be replaced by it. Both setters write to the
// selection.
function useWriteLanding(step: LandingStep) {
  const staging = usePipelineStaging();
  const { landing } = step;
  useEffect(() => {
    if (landing?.kind !== "write" || staging.stageTab !== landing.tab) return;
    if (selectOnce(step, landing, landing.row, staging)) return;
    if (staging.contentLoading) return;
    step.settle(null);
    const write =
      landing.editor === "script"
        ? staging.setScriptDocument
        : staging.setSchemaDocument;
    write(landing.code);
    staging.requestEditorFocus();
  }, [landing, staging, step]);
}

// The input document is one global text, but it is written only once
// the Source tab is up, the input editor is the visible one (a schema row
// there would hide it) and no sample fetch is in flight: landing on the
// tab can select a source row, and with an empty input that starts a
// sample load which would land over an earlier write.
function useInputLanding(step: LandingStep) {
  const staging = usePipelineStaging();
  const { landing } = step;
  useEffect(() => {
    if (landing?.kind !== "input" || staging.stageTab !== "source") return;
    const hidden = staging.editorKind !== "input";
    const row = tabDefaultRow("source", staging);
    if (hidden && selectOnce(step, landing, row, staging)) return;
    if (staging.contentLoading) return;
    step.settle(null);
    staging.setInputDocument(landing.code);
    staging.requestEditorFocus();
  }, [landing, staging, step]);
}

// The cases land in the catalog suite, then as staged rows; the first one
// opens in the viewer right away, as a click on its row would (Generate
// does the same).
function useTestsLanding(step: LandingStep) {
  const staging = usePipelineStaging();
  const { saveCases } = useSuiteCatalog();
  const ensureSuite = useEnsureStagedSuite();
  const { landing } = step;

  const land = (cases: TestSuiteCase[]) => {
    const ids = staging.appendSavedTests(
      cases.map((c) => ({
        name: c.name,
        rows: testRowsFromSuiteCase(c),
        strictMode: c.strict_mode ?? false,
      })),
      Icon.Test,
    );
    if (ids[0]) staging.openTestViewer(ids[0]);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fires once per landing, when the suite group exists
  useEffect(() => {
    if (landing?.kind !== "tests" || landing.started) return;
    if (!staging.stagedSuite) return;
    step.settle({ ...landing, started: true });
    const cases = withFreeTestNames(
      landing.cases,
      staging.tests.map((t) => t.name),
    );
    ensureSuite()
      .then((suiteId) => saveCases(suiteId, cases))
      .then(() => land(cases))
      // A failed write leaves the panel and catalog untouched, as the
      // Tests panel's own drop does.
      .catch(() => {})
      .finally(() => step.settle(null));
  }, [landing, staging.stagedSuite]);
}

// The code editor mounts with the Full view; the caret follows once it
// is there (a focus request issued before the mount would be missed).
function usePipelineLanding(step: LandingStep) {
  const staging = usePipelineStaging();
  const { landing } = step;
  useEffect(() => {
    if (landing?.kind !== "pipeline" || staging.studioView !== "full") return;
    step.settle(null);
    staging.requestEditorFocus();
  }, [landing, staging, step]);
}

function useLandings(step: LandingStep) {
  useWriteLanding(step);
  useInputLanding(step);
  useTestsLanding(step);
  usePipelineLanding(step);
}

type Click = { target: InsertTarget; code: string; name: string | null };

type Starter = (
  click: Click,
  staging: Staging,
  generatedCode: string,
) => Landing | null;

function landOn(staging: Staging, tab: StageTabId) {
  if (staging.stageTab !== tab) staging.setStageTab(tab);
}

function writeLanding(
  tab: StageTabId,
  row: string,
  editor: "script" | "schema",
  code: string,
): Landing {
  return { kind: "write", tab, row, editor, code, selected: false };
}

// The first step of each landing, on the click: the tab (or view) switch,
// plus the writes that need no selection — then the landing effects take
// over on the renders that follow.
const STARTERS: Record<InsertTarget["kind"], Starter> = {
  script: ({ target, code }, staging) => {
    if (target.kind !== "script") return null;
    landOn(staging, "transform");
    const row = target.rowId ?? TRANSFORM_EDITOR_ID;
    return writeLanding("transform", row, "script", code);
  },
  schema: ({ target, code }, staging) => {
    if (target.kind !== "schema") return null;
    landOn(staging, target.tab);
    return writeLanding(target.tab, target.slot, "schema", code);
  },
  input: ({ code }, staging) => {
    landOn(staging, "source");
    return { kind: "input", code, selected: false };
  },
  tests: ({ code, name }, staging) => {
    const cases = generatedCasesFromJson(code);
    if (!cases || cases.length === 0) return null;
    landOn(staging, "tests");
    if (!staging.stagedSuite) staging.stagePendingSuite(name || "AI tests");
    return { kind: "tests", cases, started: false };
  },
  pipeline: ({ code }, staging, generatedCode) => {
    staging.setPipelineCodeDraft({ text: code, basedOn: generatedCode });
    staging.setStudioView("full");
    return { kind: "pipeline" };
  },
};

// The click that is waiting on Replace/Cancel, and its verdicts.
function useReplaceGuard(state: InsertState, start: (click: Click) => void) {
  const [pendingClick, setPendingClick] = useState<Click | null>(null);

  const guard = useCallback(
    (click: Click) => {
      if (insertReplaces(click.target, state, click.code) === null) {
        start(click);
      } else {
        setPendingClick(click);
      }
    },
    [state, start],
  );

  const pending = useMemo<PendingReplace | null>(() => {
    if (!pendingClick) return null;
    const { target, code } = pendingClick;
    return {
      replaces: insertReplaces(target, state, code) ?? "your unsaved edits",
      destination: insertDestination(target),
    };
  }, [pendingClick, state]);

  const confirmPending = useCallback(() => {
    if (!pendingClick) return;
    setPendingClick(null);
    start(pendingClick);
  }, [pendingClick, start]);
  const cancelPending = useCallback(() => setPendingClick(null), []);

  return { guard, pending, confirmPending, cancelPending };
}

export function useApplyAiCode(generatedCode: string): InsertHost {
  const staging = usePipelineStaging();
  const state = useInsertState(generatedCode);
  const [landing, settle] = useState<Landing | null>(null);
  const step = useMemo<LandingStep>(() => ({ landing, settle }), [landing]);
  useLandings(step);

  // A pipeline landing while a dropped stage's schema still seeds waits
  // for the seed (pipelineLandingWait). The check sits in the starter, so
  // it covers every way a click gets there — straight from `apply` and
  // from the replace modal's confirm, where a drop may have started a
  // seed while the modal was open. The parked click lands as soon as
  // nothing seeds; its replace question was already answered.
  const [deferred, setDeferred] = useState<Click | null>(null);
  const start = useCallback(
    (click: Click) => {
      if (pipelineLandingWait(click.target, staging.seedingStageIds, staging)) {
        setDeferred(click);
        return;
      }
      settle(STARTERS[click.target.kind](click, staging, generatedCode));
    },
    [staging, generatedCode],
  );
  useEffect(() => {
    if (!deferred) return;
    if (pipelineLandingWait(deferred.target, staging.seedingStageIds, staging))
      return;
    setDeferred(null);
    start(deferred);
  }, [deferred, staging, start]);
  const { guard, pending, confirmPending, cancelPending } = useReplaceGuard(
    state,
    start,
  );

  // One landing at a time: a click while one is in flight is dropped
  // (they take a few renders — two quick test inserts would otherwise
  // race the same pending suite). The handler keeps one identity for the
  // chat's lifetime and reads the live state through a ref: a new
  // function per Studio render would re-render every message in the
  // conversation on each tab switch or keystroke.
  const live = useRef({ landing, state, guard });
  live.current = { landing, state, guard };
  const apply = useCallback(
    (
      code: string,
      lang: string,
      artifact: string | null,
      name: string | null,
    ) => {
      const now = live.current;
      if (now.landing !== null) return;
      const target = insertTarget({ lang, artifact, name }, now.state);
      if (target) now.guard({ target, code, name });
    },
    [],
  );

  return useMemo(
    () => ({ apply, pending, confirmPending, cancelPending }),
    [apply, pending, confirmPending, cancelPending],
  );
}
